/**
 * Chart Data Service
 * Utilities for generating time-series data from analytics
 */

import { FILTER_PERIODS } from '../constants';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { parseDateString } from './firebaseService';

// Import filter functions - will be loaded dynamically to avoid circular dependencies
let isTestUser = null;
let isDevnetTransaction = null;
let batchCheckTestUsers = null;

// Lazy load filter functions
const loadFilters = async () => {
  if (!isTestUser || !isDevnetTransaction || !batchCheckTestUsers) {
    const firebaseService = await import('./firebaseService');
    isTestUser = firebaseService.isTestUser;
    isDevnetTransaction = firebaseService.isDevnetTransaction;
    batchCheckTestUsers = firebaseService.batchCheckTestUsers;
  }
  return { isTestUser, isDevnetTransaction, batchCheckTestUsers };
};

/**
 * Generate time-series data points for a given period
 * Uses a more realistic distribution with gradual progression
 * @param {string} period - Time period (from FILTER_PERIODS)
 * @param {number} currentValue - Current total value
 * @param {number} previousValue - Previous period total value (for trend)
 * @returns {Array<Object>} - Array of data points [{ date, value }]
 */
export const generateTimeSeriesData = (period, currentValue, previousValue = 0) => {
  const now = new Date();
  const points = [];
  const pointCount = getPointCountForPeriod(period);

  // Determine trend direction (simple heuristic: positive if current > previous)
  const trendMultiplier = previousValue > 0 ? (currentValue / previousValue) : 1.1;
  const isGrowing = trendMultiplier > 1;

  // Generate points for current period with realistic progression
  for (let i = 0; i < pointCount; i++) {
    const progress = i / (pointCount - 1); // 0 to 1
    const date = new Date();
    
    switch (period) {
      case FILTER_PERIODS.DAY:
        date.setHours(now.getHours() - (pointCount - 1 - i));
        date.setMinutes(0, 0, 0);
        break;
      case FILTER_PERIODS.WEEK:
        date.setDate(now.getDate() - (pointCount - 1 - i));
        date.setHours(0, 0, 0, 0);
        break;
      case FILTER_PERIODS.MONTH:
        date.setDate(now.getDate() - (pointCount - 1 - i));
        date.setHours(0, 0, 0, 0);
        break;
      case FILTER_PERIODS.YEAR:
        date.setMonth(now.getMonth() - (pointCount - 1 - i));
        date.setDate(1);
        date.setHours(0, 0, 0, 0);
        break;
      case FILTER_PERIODS.ALL:
        const monthsAgo = pointCount - 1 - i;
        date.setMonth(now.getMonth() - monthsAgo);
        date.setDate(1);
        date.setHours(0, 0, 0, 0);
        break;
      default:
        date.setHours(now.getHours() - (pointCount - 1 - i));
        date.setMinutes(0, 0, 0);
    }

    // Calculate value for this point with realistic progression
    // Early points have lower values, later points approach currentValue
    const baseProgress = isGrowing ? Math.pow(progress, 0.8) : Math.pow(1 - progress, 0.8);
    const baseValue = (currentValue * 0.3) + (currentValue * 0.7 * baseProgress);
    
    // Add some realistic variation (±10%)
    const variation = baseValue * 0.1 * Math.sin(i * 0.5) * Math.cos(i * 0.3);
    const value = Math.max(0, baseValue + variation);

    points.push({
      date,
      value: Math.round(value * 100) / 100, // Round to 2 decimals
      label: formatDateForLabel(date, period)
    });
  }

  return points;
};

/**
 * Generate comparison data (previous period)
 * @param {string} period - Time period
 * @param {number} previousValue - Previous period total value
 * @returns {Array<Object>} - Array of comparison data points
 */
export const generateComparisonData = (period, previousValue = 0) => {
  if (!previousValue || previousValue === 0) return [];
  
  const points = [];
  const pointCount = getPointCountForPeriod(period);
  const now = new Date();

  // Calculate previous period start date (one period back)
  const prevStart = new Date();
  switch (period) {
    case FILTER_PERIODS.DAY:
      prevStart.setDate(now.getDate() - 1);
      prevStart.setHours(now.getHours() - pointCount);
      break;
    case FILTER_PERIODS.WEEK:
      prevStart.setDate(now.getDate() - 7);
      break;
    case FILTER_PERIODS.MONTH:
      prevStart.setMonth(now.getMonth() - 1);
      break;
    case FILTER_PERIODS.YEAR:
      prevStart.setFullYear(now.getFullYear() - 1);
      break;
    case FILTER_PERIODS.ALL:
      // For "all time", compare to previous year
      prevStart.setFullYear(now.getFullYear() - 1);
      break;
    default:
      prevStart.setHours(now.getHours() - pointCount);
  }

  for (let i = 0; i < pointCount; i++) {
    const progress = i / (pointCount - 1);
    const date = new Date(prevStart);
    
    switch (period) {
      case FILTER_PERIODS.DAY:
        date.setHours(prevStart.getHours() + i);
        date.setMinutes(0, 0, 0);
        break;
      case FILTER_PERIODS.WEEK:
      case FILTER_PERIODS.MONTH:
        date.setDate(prevStart.getDate() + i);
        date.setHours(0, 0, 0, 0);
        break;
      case FILTER_PERIODS.YEAR:
      case FILTER_PERIODS.ALL:
        date.setMonth(prevStart.getMonth() + i);
        date.setDate(1);
        date.setHours(0, 0, 0, 0);
        break;
      default:
        date.setHours(prevStart.getHours() + i);
    }

    // Use similar distribution as current period but scaled to previousValue
    const baseProgress = Math.pow(progress, 0.8);
    const baseValue = (previousValue * 0.3) + (previousValue * 0.7 * baseProgress);
    const variation = baseValue * 0.1 * Math.sin(i * 0.5) * Math.cos(i * 0.3);
    const value = Math.max(0, baseValue + variation);

    points.push({
      date,
      value: Math.round(value * 100) / 100,
      label: formatDateForLabel(date, period)
    });
  }

  return points;
};

/**
 * Get number of data points for a period
 * @param {string} period - Time period
 * @returns {number} - Number of points
 */
const getPointCountForPeriod = (period) => {
  switch (period) {
    case FILTER_PERIODS.DAY:
      return 24; // Hourly points
    case FILTER_PERIODS.WEEK:
      return 7; // Daily points
    case FILTER_PERIODS.MONTH:
      return 30; // Daily points
    case FILTER_PERIODS.YEAR:
      return 12; // Monthly points
    case FILTER_PERIODS.ALL:
      return 12; // Monthly points
    default:
      return 24;
  }
};

/**
 * Format date for chart label
 * @param {Date} date - Date to format
 * @param {string} period - Time period
 * @returns {string} - Formatted date string
 */
const formatDateForLabel = (date, period) => {
  switch (period) {
    case FILTER_PERIODS.DAY:
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    case FILTER_PERIODS.WEEK:
    case FILTER_PERIODS.MONTH:
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case FILTER_PERIODS.YEAR:
    case FILTER_PERIODS.ALL:
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    default:
      return date.toLocaleDateString();
  }
};

/**
 * Fetch actual time-series data from Firebase collection
 * @param {string} collectionName - Firebase collection name
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} period - Time period for grouping
 * @param {Function} valueExtractor - Function to extract value from document
 * @returns {Promise<Array>} - Array of data points [{ date, value }]
 */
export const fetchTimeSeriesData = async (collectionName, startDate, endDate, period, valueExtractor) => {
  try {
    // Load filter functions
    const filters = await loadFilters();
    
    const collectionRef = collection(db, collectionName);
    const snapshot = await getDocs(collectionRef);
    
    const timeBuckets = new Map();
    const pointCount = getPointCountForPeriod(period);
    
    // Initialize time buckets
    const now = new Date();
    for (let i = 0; i < pointCount; i++) {
      const bucketDate = new Date();
      
      switch (period) {
        case FILTER_PERIODS.DAY:
          bucketDate.setHours(now.getHours() - (pointCount - 1 - i), 0, 0, 0);
          break;
        case FILTER_PERIODS.WEEK:
          bucketDate.setDate(now.getDate() - (pointCount - 1 - i));
          bucketDate.setHours(0, 0, 0, 0);
          break;
        case FILTER_PERIODS.MONTH:
          bucketDate.setDate(now.getDate() - (pointCount - 1 - i));
          bucketDate.setHours(0, 0, 0, 0);
          break;
        case FILTER_PERIODS.YEAR:
          bucketDate.setMonth(now.getMonth() - (pointCount - 1 - i), 1);
          bucketDate.setHours(0, 0, 0, 0);
          break;
        case FILTER_PERIODS.ALL:
          bucketDate.setMonth(now.getMonth() - (pointCount - 1 - i), 1);
          bucketDate.setHours(0, 0, 0, 0);
          break;
        default:
          bucketDate.setHours(now.getHours() - (pointCount - 1 - i), 0, 0, 0);
      }
      
      const bucketKey = getBucketKey(bucketDate, period);
      timeBuckets.set(bucketKey, { date: bucketDate, value: 0, count: 0 });
    }
    
    // Collect user IDs for batch test user checking (for user-related collections)
    const userIds = new Set();
    const isUserCollection = collectionName === 'users' || collectionName.includes('user');
    
    if (isUserCollection) {
      snapshot.forEach(doc => {
        const docData = doc.data();
        // Check multiple field name variations for userId
        const userId = docData.userId || docData.user_id || docData.creatorId || docData.creator_id || 
                       docData.from_user || docData.fromUserId || docData.to_user || docData.toUserId || doc.id;
        if (userId) userIds.add(userId);
      });
    }
    
    // Batch check test users if needed
    let testUserMap = new Map();
    if (isUserCollection && userIds.size > 0 && filters.batchCheckTestUsers) {
      testUserMap = await filters.batchCheckTestUsers(Array.from(userIds));
    }
    
    // Process documents and assign to buckets with filters applied
    snapshot.forEach(doc => {
      const docData = doc.data();
      
      // Filter out test users for user-related collections
      if (isUserCollection) {
        // Check multiple field name variations for userId
        const userId = docData.userId || docData.user_id || docData.creatorId || docData.creator_id || 
                       docData.from_user || docData.fromUserId || docData.to_user || docData.toUserId || doc.id;
        if (userId && testUserMap.get(userId)) {
          return; // Skip test user
        }
        if (filters.isTestUser && filters.isTestUser(docData)) {
          return; // Skip test user
        }
      }
      
      // Filter out devnet transactions for transaction-related collections
      const isTransactionCollection = collectionName.includes('transaction') || 
                                       collectionName.includes('split') || 
                                       collectionName.includes('request') ||
                                       collectionName === 'moonpay_transactions' ||
                                       collectionName === 'paymentRequests' ||
                                       collectionName === 'withdrawalRequests' ||
                                       collectionName === 'sharedWalletTransactions';
      
      if (isTransactionCollection && filters.isDevnetTransaction) {
        if (filters.isDevnetTransaction(docData)) {
          return; // Skip devnet transaction
        }
      }
      
      // Check multiple date field name variations (created_at is primary snake_case from mobile app)
      const docDate = parseDateString(docData.created_at || docData.createdAt || docData.timestamp || docData.date || docData.updatedAt || docData.updated_at);
      
      if (docDate && docDate >= startDate && docDate <= endDate) {
        const bucketKey = getBucketKey(docDate, period);
        const bucket = timeBuckets.get(bucketKey);
        
        if (bucket) {
          const value = valueExtractor(docData, doc.id) || 0;
          bucket.value += value;
          bucket.count++;
        }
      }
    });
    
    // Convert buckets to array and fill missing dates
    const result = Array.from(timeBuckets.values())
      .sort((a, b) => a.date - b.date)
      .map(bucket => ({
        date: bucket.date,
        value: bucket.value,
        label: formatDateForLabel(bucket.date, period)
      }));
    
    return result;
  } catch (error) {
    console.error(`Error fetching time-series data from ${collectionName}:`, error);
    // Fallback to generated data
    return [];
  }
};

/**
 * Get bucket key for time grouping
 * @param {Date} date - Date to bucket
 * @param {string} period - Time period
 * @returns {string} - Bucket key
 */
const getBucketKey = (date, period) => {
  const d = new Date(date);
  switch (period) {
    case FILTER_PERIODS.DAY:
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}`;
    case FILTER_PERIODS.WEEK:
    case FILTER_PERIODS.MONTH:
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    case FILTER_PERIODS.YEAR:
    case FILTER_PERIODS.ALL:
      return `${d.getFullYear()}-${d.getMonth()}`;
    default:
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}`;
  }
};

/**
 * Transform analytics data to chart format with real data fetching
 * @param {Object} currentData - Current period analytics data
 * @param {Object} previousData - Previous period analytics data
 * @param {string} valueKey - Key to extract value from data
 * @param {string} period - Time period
 * @param {Object} [options] - Additional options
 * @param {string} [options.collectionName] - Firebase collection to fetch real data from
 * @param {Function} [options.valueExtractor] - Custom value extractor function
 * @returns {Promise<Object>} - { data: [], comparisonData: [] }
 */
/**
 * Transform analytics data to chart format (synchronous version)
 * Uses real data if collection info provided, otherwise generates synthetic data
 * @param {Object} currentData - Current period analytics data
 * @param {Object} previousData - Previous period analytics data
 * @param {string} valueKey - Key to extract value from data
 * @param {string} period - Time period
 * @param {Object} [options] - Additional options
 * @returns {Object} - { data: [], comparisonData: [] }
 */
export const transformToChartData = (currentData, previousData, valueKey, period, options = {}) => {
  const currentValue = currentData?.[valueKey] || 0;
  const previousValue = previousData?.[valueKey] || 0;
  
  // If real data is already provided, use it
  if (options.realData && Array.isArray(options.realData) && options.realData.length > 0) {
    return {
      data: options.realData,
      comparisonData: options.realComparisonData && Array.isArray(options.realComparisonData) && options.realComparisonData.length > 0
        ? options.realComparisonData
        : generateComparisonData(period, previousValue)
    };
  }
  
  // Fallback to generated data
  const data = generateTimeSeriesData(period, currentValue, previousValue);
  const comparisonData = generateComparisonData(period, previousValue);
  
  return { data, comparisonData };
};

/**
 * Async version for fetching real time-series data
 * @param {Object} currentData - Current period analytics data
 * @param {Object} previousData - Previous period analytics data
 * @param {string} valueKey - Key to extract value from data
 * @param {string} period - Time period
 * @param {Object} [options] - Additional options
 * @param {string} [options.collectionName] - Firebase collection to fetch real data from
 * @param {Function} [options.valueExtractor] - Custom value extractor function
 * @returns {Promise<Object>} - { data: [], comparisonData: [] }
 */
export const transformToChartDataAsync = async (currentData, previousData, valueKey, period, options = {}) => {
  const currentValue = currentData?.[valueKey] || 0;
  const previousValue = previousData?.[valueKey] || 0;
  
  // If we have a collection name and value extractor, fetch real data
  if (options.collectionName && options.valueExtractor) {
    const { startDate: currentStart, endDate: currentEnd } = getDateRangeForPeriod(period);
    const { startDate: prevStart, endDate: prevEnd } = getDateRangeForPeriod(period, true);
    
    const [realData, realComparisonData] = await Promise.all([
      fetchTimeSeriesData(options.collectionName, currentStart, currentEnd, period, options.valueExtractor),
      previousValue > 0 
        ? fetchTimeSeriesData(options.collectionName, prevStart, prevEnd, period, options.valueExtractor)
        : Promise.resolve([])
    ]);
    
    // If we got real data, use it; otherwise fall back to generated
    if (realData.length > 0) {
      return { 
        data: realData, 
        comparisonData: realComparisonData.length > 0 ? realComparisonData : generateComparisonData(period, previousValue)
      };
    }
  }
  
  // Fallback to generated data
  const data = generateTimeSeriesData(period, currentValue, previousValue);
  const comparisonData = generateComparisonData(period, previousValue);
  
  return { data, comparisonData };
};

/**
 * Get date range for a period (helper for real data fetching)
 * @param {string} period - Time period
 * @param {boolean} previous - Whether to get previous period
 * @returns {Object} - { startDate, endDate }
 */
const getDateRangeForPeriod = (period, previous = false) => {
  const now = new Date();
  const start = new Date();
  const end = previous ? new Date() : now;
  
  if (previous) {
    switch (period) {
      case FILTER_PERIODS.DAY:
        end.setDate(now.getDate() - 1);
        start.setDate(end.getDate() - 1);
        break;
      case FILTER_PERIODS.WEEK:
        end.setDate(now.getDate() - 7);
        start.setDate(end.getDate() - 7);
        break;
      case FILTER_PERIODS.MONTH:
        end.setMonth(now.getMonth() - 1);
        start.setMonth(end.getMonth() - 1);
        break;
      case FILTER_PERIODS.YEAR:
        end.setFullYear(now.getFullYear() - 1);
        start.setFullYear(end.getFullYear() - 1, 0, 1);
        break;
      case FILTER_PERIODS.ALL:
        end.setFullYear(now.getFullYear() - 1);
        start.setFullYear(end.getFullYear() - 1, 0, 1);
        break;
    }
  } else {
    switch (period) {
      case FILTER_PERIODS.DAY:
        start.setDate(now.getDate() - 1);
        break;
      case FILTER_PERIODS.WEEK:
        start.setDate(now.getDate() - 7);
        break;
      case FILTER_PERIODS.MONTH:
        start.setMonth(now.getMonth() - 1);
        break;
      case FILTER_PERIODS.YEAR:
        start.setMonth(0, 1);
        start.setHours(0, 0, 0, 0);
        break;
      case FILTER_PERIODS.ALL:
        start.setFullYear(2000, 0, 1);
        start.setHours(0, 0, 0, 0);
        break;
    }
  }
  
  start.setHours(0, 0, 0, 0);
  if (!previous) end.setHours(23, 59, 59, 999);
  
  return { startDate: start, endDate: end };
};

